/**
 * HealthSyncService — Apple HealthKit (iOS) + Health Connect (Android)
 *
 * Reads: weight, steps, resting heart rate
 * Writes: completed workouts, weight measurements
 *
 * All methods are no-ops on web. Uses dynamic plugin access via window.Capacitor.
 *
 * Requires (installed via setup-capacitor.sh when native build):
 *   @capacitor-community/health-kit
 *   @capacitor-community/health-connect
 */

import { isNative, isIOS, isAndroid } from '../utils/platform';

function getHealthKitPlugin() {
  return window.Capacitor?.Plugins?.HealthKit || null;
}

function getHealthConnectPlugin() {
  return window.Capacitor?.Plugins?.HealthConnect || null;
}

export class HealthSyncService {

  // ─── Permission ───────────────────────────────────────────────

  static async requestPermissions() {
    if (!isNative) return false;

    try {
      if (isIOS) {
        const hk = getHealthKitPlugin();
        if (!hk) return false;
        await hk.requestAuthorization({
          all: [],
          read: [
            'HKQuantityTypeIdentifierBodyMass',
            'HKQuantityTypeIdentifierHeartRate',
            'HKQuantityTypeIdentifierStepCount',
            'HKQuantityTypeIdentifierActiveEnergyBurned',
            'HKQuantityTypeIdentifierDistanceWalkingRunning',
            'HKCategoryTypeIdentifierSleepAnalysis',
          ],
          write: [
            'HKQuantityTypeIdentifierBodyMass',
            'HKWorkoutTypeIdentifier',
          ],
        });
        return true;
      }

      if (isAndroid) {
        const hc = getHealthConnectPlugin();
        if (!hc) return false;
        const result = await hc.checkAvailability();
        if (result.availability !== 'Available') return false;
        await hc.requestHealthPermissions({
          read: ['Weight', 'HeartRate', 'Steps', 'ActiveCaloriesBurned', 'Distance', 'SleepSession'],
          write: ['Weight', 'ExerciseSession'],
        });
        return true;
      }
    } catch (e) {
      console.error('[HealthSync] Permission request failed:', e);
    }
    return false;
  }

  // ─── Read weight ──────────────────────────────────────────────

  static async getLatestWeight() {
    if (!isNative) return null;
    try {
      if (isIOS) {
        const hk = getHealthKitPlugin();
        if (!hk) return null;
        const results = await hk.queryQuantitySamples({
          quantityType: 'HKQuantityTypeIdentifierBodyMass',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString(),
          endDate: new Date().toISOString(),
          limit: 1,
          ascending: false,
        });
        const sample = results.samples?.[0];
        return sample ? { value: sample.quantity, unit: 'kg', date: new Date(sample.endDate) } : null;
      }
      if (isAndroid) {
        const hc = getHealthConnectPlugin();
        if (!hc) return null;
        const results = await hc.readRecords({
          type: 'Weight',
          timeRangeFilter: {
            operator: 'between',
            startTime: new Date(Date.now() - 30 * 86400000).toISOString(),
            endTime: new Date().toISOString(),
          },
          pageSize: 1,
          ascendingOrder: false,
        });
        const record = results.records?.[0];
        return record ? { value: record.weight.inKilograms, unit: 'kg', date: new Date(record.time) } : null;
      }
    } catch (e) {
      console.error('[HealthSync] getLatestWeight failed:', e);
    }
    return null;
  }

  // ─── Read steps ───────────────────────────────────────────────

  static async getTodaySteps() {
    if (!isNative) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    try {
      if (isIOS) {
        const hk = getHealthKitPlugin();
        if (!hk) return 0;
        const results = await hk.queryQuantitySamples({
          quantityType: 'HKQuantityTypeIdentifierStepCount',
          startDate: today.toISOString(),
          endDate: new Date().toISOString(),
        });
        return results.samples?.reduce((sum, s) => sum + s.quantity, 0) ?? 0;
      }
      if (isAndroid) {
        const hc = getHealthConnectPlugin();
        if (!hc) return 0;
        const results = await hc.readRecords({
          type: 'Steps',
          timeRangeFilter: { operator: 'between', startTime: today.toISOString(), endTime: new Date().toISOString() },
        });
        return results.records?.reduce((sum, r) => sum + r.count, 0) ?? 0;
      }
    } catch (e) {
      console.error('[HealthSync] getTodaySteps failed:', e);
    }
    return 0;
  }

  // ─── Read resting heart rate (iOS only) ───────────────────────

  static async getRestingHeartRate() {
    if (!isNative || !isIOS) return null;
    try {
      const hk = getHealthKitPlugin();
      if (!hk) return null;
      const results = await hk.queryQuantitySamples({
        quantityType: 'HKQuantityTypeIdentifierRestingHeartRate',
        startDate: new Date(Date.now() - 7 * 86400000).toISOString(),
        endDate: new Date().toISOString(),
        limit: 1,
        ascending: false,
      });
      return results.samples?.[0]?.quantity ?? null;
    } catch {
      return null;
    }
  }

  // ─── Write workout ────────────────────────────────────────────

  static async writeWorkout({ activityType, startDate, endDate, totalEnergyBurned, totalDistance }) {
    if (!isNative) return false;
    const iosActivityMap = { running: 37, strength: 50, cycling: 13, walking: 52, other: 3000 };
    const androidActivityMap = { running: 56, strength: 80, cycling: 8, walking: 79, other: 4 };
    try {
      if (isIOS) {
        const hk = getHealthKitPlugin();
        if (!hk) return false;
        await hk.saveWorkout({
          activityType: iosActivityMap[activityType] ?? 3000,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          totalEnergyBurned,
          totalDistance,
        });
        return true;
      }
      if (isAndroid) {
        const hc = getHealthConnectPlugin();
        if (!hc) return false;
        await hc.insertRecords({
          records: [{
            type: 'ExerciseSession',
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString(),
            exerciseType: androidActivityMap[activityType] ?? 4,
          }],
        });
        return true;
      }
    } catch (e) {
      console.error('[HealthSync] writeWorkout failed:', e);
    }
    return false;
  }

  // ─── Write weight ─────────────────────────────────────────────

  static async writeWeight(kg, date = new Date()) {
    if (!isNative) return false;
    try {
      if (isIOS) {
        const hk = getHealthKitPlugin();
        if (!hk) return false;
        await hk.saveQuantitySample({
          quantityType: 'HKQuantityTypeIdentifierBodyMass',
          unit: 'kg',
          value: kg,
          startDate: date.toISOString(),
          endDate: date.toISOString(),
        });
        return true;
      }
      if (isAndroid) {
        const hc = getHealthConnectPlugin();
        if (!hc) return false;
        await hc.insertRecords({
          records: [{ type: 'Weight', time: date.toISOString(), weight: { inKilograms: kg } }],
        });
        return true;
      }
    } catch (e) {
      console.error('[HealthSync] writeWeight failed:', e);
    }
    return false;
  }
}
